import React from 'react';
import { X, Plus } from 'lucide-react';
import FormControl from '../../common/FormControl';
import { DataSource, DataSourceLinkCreate } from '../types';
import '../FileAnalytics.css';

export type JoinType = 'inner' | 'left' | 'anti';
type ColOpt = { value: string; label: string };


export interface CombineJoinKey {
  tempId: string;
  left_col: string;
  right_col: string;
}

/** Flat config for a two-dataset combine. */
export interface CombineConfig {
  leftSourceId: string;
  rightSourceId: string;
  joinType: JoinType;
  joinKeys: CombineJoinKey[];
  leftColumns: string[];   // empty = all columns
  rightColumns: string[];  // empty = all columns
}

export function mkJoinKey(): CombineJoinKey {
  return { tempId: `t${Math.random().toString(36).slice(2, 9)}`, left_col: '', right_col: '' };
}

export function mkCombineConfig(): CombineConfig {
  return {
    leftSourceId: '',
    rightSourceId: '',
    joinType: 'left',
    joinKeys: [mkJoinKey()],
    leftColumns: [],
    rightColumns: [],
  };
}

/**
 * Convert a CombineConfig to the two DataSourceLinkCreate payloads the backend
 * expects (one link per participating source, role='combine').
 * The "new source" is the base; left = sort_order 0, right = sort_order 1.
 */
export function toCombineLinks(cfg: CombineConfig): DataSourceLinkCreate[] {
  const keys = cfg.joinKeys.map(k => ({ left_col: k.left_col, right_col: k.right_col }));
  return [
    {
      ref_source_id: Number(cfg.leftSourceId),
      role: 'combine',
      join_type: cfg.joinType,
      join_keys: keys,
      selected_columns: cfg.leftColumns.length > 0 ? cfg.leftColumns : null,
      sort_order: 0,
    },
    {
      ref_source_id: Number(cfg.rightSourceId),
      role: 'combine',
      join_type: cfg.joinType,
      join_keys: keys.map(k => ({ left_col: k.right_col, right_col: k.left_col })), // mirrored
      selected_columns: cfg.rightColumns.length > 0 ? cfg.rightColumns : null,
      sort_order: 1,
    },
  ];
}

export interface CombineDataPanelProps {
  testidPrefix: string;
  availableSources: DataSource[];
  config: CombineConfig;
  busy: boolean;
  isLocked: boolean;
  onChange(patch: Partial<CombineConfig>): void;
  onJoinKeyChange(ki: number, patch: Partial<CombineJoinKey>): void;
  onAddJoinKey(): void;
  onRemoveJoinKey(ki: number): void;
}

const JOIN_TYPES: { value: JoinType; label: string; desc: string }[] = [
  { value: 'left',  label: 'Left Join',  desc: 'All rows from Dataset A; matched rows from B' },
  { value: 'inner', label: 'Inner Join', desc: 'Only rows that match in both datasets' },
  { value: 'anti',  label: 'Anti Join',  desc: 'Only rows in Dataset A with NO match in B' },
];

function ColChips({
  label, options, selected, onChange, busy, isLocked, testid,
}: {
  label: string; options: ColOpt[]; selected: string[];
  onChange(cols: string[]): void; busy: boolean; isLocked: boolean; testid: string;
}) {
  if (options.length === 0) return null;
  const allSelected = selected.length === 0;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 5 }}>
        {label}{allSelected ? <span style={{ opacity: 0.6 }}> — all columns included</span> : ''}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }} data-testid={testid}>
        {options.map(col => {
          const checked = selected.includes(col.value);
          return (
            <button
              key={col.value}
              type="button"
              disabled={busy || isLocked}
              onClick={() => {
                const next = checked
                  ? selected.filter(c => c !== col.value)
                  : [...selected, col.value];
                onChange(next);
              }}
              data-testid={`${testid}_${col.value}`}
              style={{
                fontSize: 11, padding: '2px 8px',
                border: `1px solid ${checked ? 'var(--color-primary, #4f46e5)' : 'var(--border-default, #d1d5db)'}`,
                borderRadius: 4,
                background: checked ? 'var(--color-primary-light, #eef2ff)' : 'transparent',
                color: checked ? 'var(--color-primary, #4f46e5)' : 'var(--text-secondary, #6b7280)',
                cursor: busy || isLocked ? 'not-allowed' : 'pointer',
                transition: 'all 120ms',
              }}
            >
              {col.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CombineDataPanel: React.FC<CombineDataPanelProps> = ({
  testidPrefix, availableSources, config, busy, isLocked,
  onChange, onJoinKeyChange, onAddJoinKey, onRemoveJoinKey,
}) => {
  const sourceOptions = availableSources.map(s => ({ value: String(s.id), label: s.name }));
  const leftSrc  = availableSources.find(s => String(s.id) === config.leftSourceId);
  const rightSrc = availableSources.find(s => String(s.id) === config.rightSourceId);

  const leftColOpts  = (leftSrc?.standard_schema  ?? []).map((c: any) => ({ value: c.name, label: c.display_name || c.name }));
  const rightColOpts = (rightSrc?.standard_schema ?? []).map((c: any) => ({ value: c.name, label: c.display_name || c.name }));

  const leftName  = leftSrc?.name  || 'Dataset A';
  const rightName = rightSrc?.name || 'Dataset B';

  const sectionStyle: React.CSSProperties = {
    padding: '12px 14px',
    border: '1px solid var(--border-default, #e5e7eb)',
    borderRadius: 8,
    background: 'var(--bg-subtle, #f9fafb)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };

  const sectionLabel = (n: string, num: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
      <span style={{
        width: 20, height: 20, borderRadius: '50%',
        background: 'var(--color-primary, #4f46e5)', color: '#fff',
        fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{num}</span>
      <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)' }}>{n}</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Section 1: Pick two datasets ─────────────────────────── */}
      <div style={sectionStyle}>
        {sectionLabel('Choose the two datasets to join', '1')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'end' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Dataset A (left)</div>
            <FormControl.SearchSelect
              noMargin
              value={config.leftSourceId}
              onChange={val => onChange({ leftSourceId: String(val ?? ''), leftColumns: [], joinKeys: [mkJoinKey()] })}
              options={[{ value: '', label: '— select dataset —' }, ...sourceOptions.filter(o => o.value !== config.rightSourceId)]}
              disabled={busy || isLocked}
              data-testid={`SEL_${testidPrefix}_left_source`}
            />
          </div>
          <div style={{ fontSize: 22, color: 'var(--text-muted)', textAlign: 'center', paddingBottom: 4 }}>⊕</div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Dataset B (right)</div>
            <FormControl.SearchSelect
              noMargin
              value={config.rightSourceId}
              onChange={val => onChange({ rightSourceId: String(val ?? ''), rightColumns: [], joinKeys: [mkJoinKey()] })}
              options={[{ value: '', label: '— select dataset —' }, ...sourceOptions.filter(o => o.value !== config.leftSourceId)]}
              disabled={busy || isLocked}
              data-testid={`SEL_${testidPrefix}_right_source`}
            />
          </div>
        </div>
      </div>

      {/* ── Section 2: Join type ──────────────────────────────────── */}
      <div style={sectionStyle}>
        {sectionLabel('How should the rows be matched?', '2')}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {JOIN_TYPES.map(jt => {
            const active = config.joinType === jt.value;
            return (
              <button
                key={jt.value}
                type="button"
                onClick={() => onChange({ joinType: jt.value })}
                disabled={busy || isLocked}
                title={jt.desc}
                data-testid={`BTN_${testidPrefix}_jointype_${jt.value}`}
                style={{
                  padding: '6px 14px', borderRadius: 6,
                  border: `2px solid ${active ? 'var(--color-primary, #4f46e5)' : 'var(--border-default, #e5e7eb)'}`,
                  background: active ? 'var(--color-primary-light, #eef2ff)' : 'transparent',
                  color: active ? 'var(--color-primary, #4f46e5)' : 'var(--text-secondary, #6b7280)',
                  fontSize: 12, fontWeight: 600, cursor: busy || isLocked ? 'not-allowed' : 'pointer',
                  transition: 'all 120ms',
                }}
              >{jt.label}</button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {JOIN_TYPES.find(j => j.value === config.joinType)?.desc}
        </div>
      </div>

      {/* ── Section 3: Join keys ──────────────────────────────────── */}
      <div style={sectionStyle}>
        {sectionLabel(`Match rows: ${leftName} = ${rightName}`, '3')}
        {config.joinKeys.map((k, ki) => (
          <div
            key={k.tempId}
            style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto', gap: 6, alignItems: 'center' }}
          >
            <FormControl.SearchSelect
              noMargin
              value={k.left_col}
              onChange={val => onJoinKeyChange(ki, { left_col: String(val ?? '') })}
              options={[{ value: '', label: `column in ${leftName}` }, ...leftColOpts]}
              disabled={busy || isLocked}
              data-testid={`SEL_${testidPrefix}_key_left_${ki}`}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 700, padding: '0 2px' }}>=</span>
            <FormControl.SearchSelect
              noMargin
              value={k.right_col}
              onChange={val => onJoinKeyChange(ki, { right_col: String(val ?? '') })}
              options={[{ value: '', label: `column in ${rightName}` }, ...rightColOpts]}
              disabled={busy || isLocked}
              data-testid={`SEL_${testidPrefix}_key_right_${ki}`}
            />
            {!isLocked && config.joinKeys.length > 1 ? (
              <button
                type="button"
                className="tree-list-action tree-list-action--delete"
                disabled={busy}
                title="Remove key"
                onClick={() => onRemoveJoinKey(ki)}
                data-testid={`BTN_${testidPrefix}_remove_key_${ki}`}
              ><X size={12} /></button>
            ) : <span />}
          </div>
        ))}
        {!isLocked && (
          <button
            type="button"
            style={{ fontSize: 11, color: 'var(--color-primary, #4f46e5)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={onAddJoinKey}
            disabled={busy}
            data-testid={`BTN_${testidPrefix}_add_key`}
          >
            <Plus size={12} /> Add another key
          </button>
        )}
      </div>

      {/* ── Section 4: Column selection ───────────────────────────── */}
      <div style={sectionStyle}>
        {sectionLabel('Which columns to include in the result?', '4')}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Click columns to include them. Leave all un-selected to include all columns from that dataset.
        </div>
        <ColChips
          label={`From ${leftName}`}
          options={leftColOpts}
          selected={config.leftColumns}
          onChange={cols => onChange({ leftColumns: cols })}
          busy={busy} isLocked={isLocked}
          testid={`GRP_${testidPrefix}_left_cols`}
        />
        <ColChips
          label={`From ${rightName}`}
          options={rightColOpts}
          selected={config.rightColumns}
          onChange={cols => onChange({ rightColumns: cols })}
          busy={busy} isLocked={isLocked}
          testid={`GRP_${testidPrefix}_right_cols`}
        />
        {leftColOpts.length === 0 && rightColOpts.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Select both datasets above to see available columns.
          </div>
        )}
      </div>

    </div>
  );
};

export default CombineDataPanel;
